"use client";

import type {
  FirestoreDataConverter,
  QueryConstraint,
} from "firebase/firestore";
import { useEffect, useRef } from "react";
import { useFetchCollection as useFetchCollectionShared } from "shared";

import { logClientError } from "../actions/logging-actions";
import { db } from "../firebase/clientApp";

/**
 * Wrapper hook for useFetchCollection that injects the local Firestore db instance.
 *
 * This wrapper is necessary because:
 * 1. The shared hook needs a Firestore db instance, but each package (web/management)
 *    has its own Firebase configuration and db instance
 * 2. This pattern allows us to share the hook implementation logic while keeping
 *    package-specific Firebase configurations separate
 * 3. It maintains backward compatibility - existing code continues to work without
 *    needing to pass db explicitly
 *
 * @see packages/shared/src/lib/hooks/useFetchCollection.ts for the shared implementation
 */
type useFetchCollectionProp<T> = {
  path: string;
  converter: FirestoreDataConverter<T>;
  query?: QueryConstraint | QueryConstraint[];
  realtime?: boolean;
  skip?: boolean;
  source?: string;
};

export const useFetchCollection = <T>(props: useFetchCollectionProp<T>) => {
  const result = useFetchCollectionShared({
    ...props,
    db,
  });

  // Track if we've already logged this error to avoid duplicate logs
  const lastLoggedErrorRef = useRef<string | null>(null);

  // Log errors to server when they occur
  useEffect(() => {
    if (result.error && result.error !== lastLoggedErrorRef.current) {
      lastLoggedErrorRef.current = result.error;
      logClientError({
        message: result.error,
        context: "useFetchCollection",
        metadata: {
          source: props.source,
          path: props.path,
          hasQuery: !!props.query,
          realtime: props.realtime,
        },
      });
    }
  }, [result.error, props.source, props.path, props.query, props.realtime]);

  return result;
};
